import { doc, collection, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// ── Travel credits ────────────────────────────────────────────────────────────
// A travel credit is money the client has already paid on an earlier trip whose services were
// cancelled or deferred. It does NOT reduce the trip it came from — that trip was paid in full —
// it is carried forward and reduces what the client is billed on a later trip for the same
// passenger. Trip A bills $100 and yields a $50 credit; trip B costs $100, applies the $50, bills
// $50. The client has spent $150.
//
// Amounts are stored GST-inclusive, matching how STX enter them. gstRate mirrors the convention
// already used by trip fees (`fee.gstRate ?? 0.1`) so a GST-free credit can be recorded as 0.

export const CREDIT_STATUS = { ACTIVE: 'active', EXHAUSTED: 'exhausted', VOID: 'void' };

/** Cents, to keep balance arithmetic off binary floating point. */
const cents = (v) => Math.round((parseFloat(v) || 0) * 100);
const fromCents = (c) => c / 100;

/** Is this credit still usable? Void credits never are, exhausted ones have nothing left. */
export function isCreditAvailable(credit) {
  return credit?.status !== CREDIT_STATUS.VOID && cents(credit?.remainingInclGST) > 0;
}

/** Remaining balance across a set of credits, GST-inclusive. */
export function creditBalance(credits = []) {
  return fromCents(credits.filter(isCreditAvailable).reduce((s, c) => s + cents(c.remainingInclGST), 0));
}

/** The credits belonging to a passenger that still have something left on them, oldest first. */
export function availableCreditsFor(credits = [], passengerId) {
  if (!passengerId) return [];
  return credits
    .filter(c => c.passengerId === passengerId && isCreditAvailable(c))
    .sort((a, b) => String(a.createdAt?.seconds ?? a.createdAt ?? '')
      .localeCompare(String(b.createdAt?.seconds ?? b.createdAt ?? '')));
}

/** GST-exclusive value of a GST-inclusive credit amount. */
export function creditExGST(amountInclGST, gstRate = 0.1) {
  return (parseFloat(amountInclGST) || 0) / (1 + (gstRate ?? 0.1));
}

/**
 * Validate a set of proposed applications against the credits they draw on and the trip's gross
 * value. Returns { ok, error, totalInclGST }.
 *
 * `proposed` is [{ creditId, amountInclGST }]. `tripGrossInclGST` caps the total: a credit can
 * take a trip to zero but never below it, or we'd be invoicing the client a negative amount and
 * silently burning credit they could have used on a later trip.
 */
export function validateCreditApplications(proposed = [], credits = [], tripGrossInclGST = 0) {
  const byId = new Map(credits.map(c => [c.id, c]));
  let totalCents = 0;

  for (const p of proposed) {
    const amt = cents(p.amountInclGST);
    if (amt <= 0) continue;
    const credit = byId.get(p.creditId);
    if (!credit) return { ok: false, error: 'That travel credit no longer exists.', totalInclGST: 0 };
    if (credit.status === CREDIT_STATUS.VOID)
      return { ok: false, error: `The credit "${credit.reason || credit.id}" has been voided.`, totalInclGST: 0 };
    if (amt > cents(credit.remainingInclGST)) {
      return {
        ok: false,
        error: `Only $${fromCents(cents(credit.remainingInclGST)).toFixed(2)} remains on that credit.`,
        totalInclGST: 0,
      };
    }
    totalCents += amt;
  }

  if (totalCents > cents(tripGrossInclGST)) {
    return {
      ok: false,
      error: `Credit applied ($${fromCents(totalCents).toFixed(2)}) cannot exceed the trip total ` +
             `($${(parseFloat(tripGrossInclGST) || 0).toFixed(2)}).`,
      totalInclGST: 0,
    };
  }

  return { ok: true, error: '', totalInclGST: fromCents(totalCents) };
}

/**
 * Spread an amount across credits oldest-first, returning [{ creditId, amountInclGST }].
 * Used by the "use all available credit" shortcut.
 */
export function allocateOldestFirst(amountInclGST, credits = []) {
  let left = cents(amountInclGST);
  const out = [];
  for (const c of credits) {
    if (left <= 0) break;
    const take = Math.min(left, cents(c.remainingInclGST));
    if (take <= 0) continue;
    out.push({ creditId: c.id, amountInclGST: fromCents(take) });
    left -= take;
  }
  return out;
}

/** The creditsApplied entries a trip should carry, given the proposed applications. */
export function buildCreditsApplied(proposed = [], credits = [], user) {
  const byId = new Map(credits.map(c => [c.id, c]));
  const at   = new Date().toISOString();
  return proposed
    .filter(p => cents(p.amountInclGST) > 0)
    .map(p => {
      const credit = byId.get(p.creditId);
      return {
        creditId:       p.creditId,
        amountInclGST:  fromCents(cents(p.amountInclGST)),
        gstRate:        credit?.gstRate ?? 0.1,
        reason:         credit?.reason || '',
        appliedAt:      at,
        appliedBy:      user?.uid  || '',
        appliedByName:  [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '',
      };
    });
}

/**
 * Move a trip from whatever credit it currently has applied to `proposed`, atomically.
 *
 * Re-reads every affected credit inside the transaction and works from the delta, so this is safe
 * to call on an edit as well as a first application — re-saving a trip that already draws $50 from
 * a credit must not take another $50. Two STX users applying the same credit at once resolve
 * through the transaction rather than both succeeding and overdrawing it.
 *
 * Writes both sides: the balance and audit trail live on the credit, while the trip carries its
 * own creditsApplied so every total, report and invoice can be computed without a second read.
 */
export async function syncTripCreditApplications({ clientId, tripId, tripRef, proposed, user }) {
  const wanted = new Map();
  for (const p of (proposed || [])) {
    const amt = cents(p.amountInclGST);
    if (amt > 0) wanted.set(p.creditId, (wanted.get(p.creditId) || 0) + amt);
  }

  return runTransaction(db, async (tx) => {
    const tripRefDoc = doc(db, 'clients', clientId, 'trips', tripId);
    const tripSnap   = await tx.get(tripRefDoc);
    if (!tripSnap.exists()) throw new Error('Trip not found.');

    const existing = new Map();
    for (const c of (tripSnap.data().creditsApplied || [])) {
      const amt = cents(c.amountInclGST);
      if (amt > 0) existing.set(c.creditId, (existing.get(c.creditId) || 0) + amt);
    }

    // Every credit touched on either side of the change
    const ids = [...new Set([...wanted.keys(), ...existing.keys()])];

    // Firestore transactions require all reads before any write.
    const snaps = new Map();
    for (const id of ids) {
      const ref  = doc(db, 'clients', clientId, 'travelCredits', id);
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('That travel credit no longer exists.');
      snaps.set(id, { ref, data: snap.data() });
    }

    const appliedEntries = [];
    const at = new Date().toISOString();

    for (const id of ids) {
      const { ref, data } = snaps.get(id);
      const want = wanted.get(id)   || 0;
      const have = existing.get(id) || 0;
      const delta = want - have;          // >0 draws down, <0 returns credit

      const remaining = cents(data.remainingInclGST) - delta;
      if (remaining < 0) {
        throw new Error(
          `Only $${fromCents(cents(data.remainingInclGST) + have).toFixed(2)} is available on the ` +
          `credit "${data.reason || id}".`
        );
      }
      if (delta > 0 && data.status === CREDIT_STATUS.VOID) {
        throw new Error(`The credit "${data.reason || id}" has been voided.`);
      }

      // One application entry per trip per credit, rewritten in place rather than appended, so
      // editing a trip doesn't leave a trail of superseded amounts that no longer sum to the
      // drawn-down balance.
      const applications = (data.applications || []).filter(a => a.tripId !== tripId);
      if (want > 0) {
        applications.push({
          tripId,
          tripRef:       tripRef || '',
          amountInclGST: fromCents(want),
          appliedAt:     at,
          appliedBy:     user?.uid || '',
          appliedByName: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '',
        });
      }

      tx.update(ref, {
        remainingInclGST: fromCents(remaining),
        applications,
        // A voided credit stays voided even once its balance moves.
        status: data.status === CREDIT_STATUS.VOID
          ? CREDIT_STATUS.VOID
          : (remaining === 0 ? CREDIT_STATUS.EXHAUSTED : CREDIT_STATUS.ACTIVE),
        updatedAt: serverTimestamp(),
      });

      if (want > 0) {
        appliedEntries.push({
          creditId:      id,
          amountInclGST: fromCents(want),
          gstRate:       data.gstRate ?? 0.1,
          reason:        data.reason || '',
          appliedAt:     at,
          appliedBy:     user?.uid || '',
          appliedByName: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '',
        });
      }
    }

    tx.update(tripRefDoc, { creditsApplied: appliedEntries, updatedAt: new Date().toISOString() });
    return appliedEntries;
  });
}

/**
 * Void a credit — it should never have been issued, or was issued wrong.
 *
 * Cancels whatever is LEFT on it. Amounts already applied to trips stay applied: those trips may
 * well have been invoiced, and silently pushing a client's bill back up months later is worse than
 * leaving an accurate record of what happened. To undo an applied amount, remove it from the trip
 * first — `syncTripCreditApplications` still allows returning credit to a voided one, it just
 * won't let any more be drawn.
 *
 * Deliberately one-way. A credit voided by mistake is fixed by issuing a fresh one, which leaves
 * both the error and the correction on the record rather than erasing the error.
 */
export function voidTravelCredit({ clientId, creditId, reason, user }) {
  if (!reason?.trim()) return Promise.reject(new Error('A reason is required to void a credit.'));

  return runTransaction(db, async (tx) => {
    const ref  = doc(db, 'clients', clientId, 'travelCredits', creditId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('That travel credit no longer exists.');

    const data = snap.data();
    if (data.status === CREDIT_STATUS.VOID) throw new Error('That credit is already voided.');

    tx.update(ref, {
      status:        CREDIT_STATUS.VOID,
      voidReason:    reason.trim(),
      voidedAt:      serverTimestamp(),
      voidedBy:      user?.uid || '',
      voidedByName:  [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '',
      // What the void actually cancelled, captured before any later return inflates remaining.
      voidedAmountInclGST: fromCents(cents(data.remainingInclGST)),
      updatedAt:     serverTimestamp(),
    });
  });
}

/** Issue a new credit against a passenger. STX only — enforced by firestore.rules. */
export function createTravelCredit({ clientId, data, user }) {
  const amount = fromCents(cents(data.amountInclGST));
  return runTransaction(db, async (tx) => {
    const ref = doc(collection(db, 'clients', clientId, 'travelCredits'));
    tx.set(ref, {
      passengerId:      data.passengerId,
      passengerName:    data.passengerName || '',
      amountInclGST:    amount,
      remainingInclGST: amount,
      gstRate:          data.gstRate ?? 0.1,
      reason:           data.reason || '',
      sourceTripIds:    data.sourceTripIds  || [],
      sourceTripRefs:   data.sourceTripRefs || [],
      status:           CREDIT_STATUS.ACTIVE,
      applications:     [],
      createdBy:        user?.uid || '',
      createdByName:    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '',
      createdAt:        serverTimestamp(),
      updatedAt:        serverTimestamp(),
    });
    return ref.id;
  });
}
